import { IconButton } from "@smartsuite/react-ui/lib/components/buttons/IconButton";
import { GrayColors, AppField, WrappedRecord, BaseRecord } from "@smartsuite/types";
import React, {
  Context,
  FunctionComponent,
  memo,
  MutableRefObject,
  useCallback,
  useContext,
} from "react";
import {
  GridHoveringContext,
  GridHoveringContextValue,
} from "@smartsuite/grid/lib/src/components/GridHovering/context";
import { Placements, Tooltip } from "@smartsuite/react-ui/lib";
import { useObservableState } from "observable-hooks";
import {
  GridViewGridRefInstance,
  GridViewSettings,
} from "@smartsuite/gridview/lib/components/GridView/GridView.types";
import { GridViewContext } from "@smartsuite/gridview/lib/components/GridView/GridView.context";

import { $t } from "../../../../../../../utils/intl";
import { isBacklinkRecord } from "../linkedRecordGrid.helpers";
import { useTableRowActionsPanelPosition } from "./useTableRowActionsPanelPosition";
import { linkedRecordRowActionsText } from "./LinkedRecordTableRowActions.text";

import "./LinkedRecordTableRowActions.sass";

const placements: Placements = ["top", "bottom"];

interface LinkedRecordTableRowActionsProps {
  canUnlinkRecord: boolean;
  gridRef: MutableRefObject<GridViewGridRefInstance>;
  canRemove?: boolean;
  onUnlink: (record: BaseRecord) => void;
}

interface TableRowButtonsProps {
  top: number;
  record: WrappedRecord;
  settings: GridViewSettings;
  canUnlinkRecord: boolean;
  onUnlink: (record: BaseRecord) => void;
}

const LinkedRecordTableRowActionsRaw: FunctionComponent<LinkedRecordTableRowActionsProps> = ({
  onUnlink,
  gridRef,
  canUnlinkRecord,
}) => {
  const {
    data: { settings },
  } = useContext(GridViewContext);

  const gridScrollTop = gridRef.current?.scrollPosition?.top ?? 0;

  const { hoveredRecord$, setHoveredElements } = useContext(
    GridHoveringContext as Context<GridHoveringContextValue<WrappedRecord, AppField>>
  );
  const [hoveredRecord] = useObservableState(() => hoveredRecord$, null);

  const handleUnlink = useCallback(
    (record: BaseRecord) => {
      setHoveredElements(undefined, undefined);
      onUnlink(record);
    },
    [onUnlink, setHoveredElements]
  );

  const positionTop = useTableRowActionsPanelPosition(hoveredRecord ?? null);
  const hoverTop = positionTop != null ? positionTop - gridScrollTop : null;
  if (!hoverTop || !hoveredRecord) {
    return null;
  }

  return (
    <TableRowButtons
      canUnlinkRecord={canUnlinkRecord}
      top={hoverTop}
      record={hoveredRecord}
      settings={settings}
      onUnlink={handleUnlink}
    />
  );
};

const TableRowButtonsRaw: FunctionComponent<TableRowButtonsProps> = ({
  top,
  record,
  canUnlinkRecord,
  onUnlink,
}) => {
  const isBacklink = record && isBacklinkRecord(record.original);

  const handleUnlink = useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      onUnlink(record.original);
    },
    [onUnlink, record.original]
  );

  if (!canUnlinkRecord || isBacklink) {
    return null;
  }

  return (
    <div className="table-row-actions" style={{ top }} aria-label="Table Row Actions">
      <Tooltip
        placements={placements}
        padding={5}
        text={$t(linkedRecordRowActionsText.unlinkTooltip)}
        target={
          <IconButton
            className="table-row-actions__remove"
            name="close-thin"
            ariaLabel="Unlink Row Button"
            size="S"
            color={GrayColors["G-8"]}
            hoverColor={GrayColors["G-8"]}
            backgroundHoverColor={GrayColors["G-1"]}
            onClick={handleUnlink}
          />
        }
      />
    </div>
  );
};

const TableRowButtons = memo(TableRowButtonsRaw);
export const LinkedRecordTableRowActions = memo(LinkedRecordTableRowActionsRaw);
